import { Loading } from '@/components/loaders';
import { EmailCampaignGateCard } from '@/features/campaigns/EmailCampaign/components/EmailCampaignGateCard';
import { EMAIL_CONNECTOR_NAME } from '@/features/campaigns/EmailCampaign/constants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useModalContext } from '@/hooks/useModalContext';
import { useCompanyValues } from '@/providers';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmailSendingDomain } from '@/models/emailSendingDomains';
import { EmailSendingDomainDetailView } from './EmailSendingDomainDetailView';
import { EmailSendingDomainInsertFlow } from './EmailSendingDomainInsertFlow';
import { EmailSendingDomainsEmptyCard } from './EmailSendingDomainsEmptyCard';
import { EmailSendingDomainsErrorCard } from './EmailSendingDomainsErrorCard';
import { EmailSendingDomainsList } from './EmailSendingDomainsList';
import { DeleteEmailSendingDomainModal } from './DeleteEmailSendingDomainModal';
import { useEmailSendingDomains } from './useEmailSendingDomains';

type DomainView = 'empty' | 'list' | 'add' | 'detail';

type EmailDomainsLocationState = {
  openAddForm?: boolean;
};

export const EmailSendingDomainsView: FC = () => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const { openModal } = useModalContext();
  const history = useHistory();
  const location = useLocation<{ openAddForm?: boolean }>();
  const initialOpenAddForm = (location.state as EmailDomainsLocationState | undefined)?.openAddForm ?? false;
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [viewOverride, setViewOverride] = useState<DomainView | null>(initialOpenAddForm ? 'add' : null);
  const [isRefreshingAfterCreate, setIsRefreshingAfterCreate] = useState(false);

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );
  const isMailtrapConnected = customConnectors?.find((connector) => connector.name === EMAIL_CONNECTOR_NAME)?.isAuthenticated ?? false;

  const { domains, isLoading, isFetching, isError, hasFetched, missingAudience, refetch, deleteFlowId, connectorInstanceId } = useEmailSendingDomains({
    skip: !workspace.id || !isMailtrapConnected,
  });

  useEffect(() => {
    const state = location.state as EmailDomainsLocationState | undefined;
    if (state?.openAddForm) {
      setViewOverride('add');
      history.replace(location.pathname);
    }
  }, [history, location.pathname, location.state]);

  const selectedDomainNameHint = useMemo(() => domains.find((domain) => domain.id === selectedDomainId)?.name, [domains, selectedDomainId]);

  const resolvedView: DomainView = useMemo(() => {
    if (viewOverride) {
      return viewOverride;
    }
    if (selectedDomainId) {
      return 'detail';
    }
    if (domains.length === 0) {
      return 'empty';
    }
    return 'list';
  }, [domains.length, selectedDomainId, viewOverride]);

  const isAddView = resolvedView === 'add';
  const isDomainsDataPending = isMailtrapConnected && (isLoading || !hasFetched);
  const showPageLoading = isConnectorsLoading || isDomainsDataPending || isRefreshingAfterCreate;
  const clearViewOverride = useCallback(() => setViewOverride(null), []);

  const handleOpenAdd = () => {
    setSelectedDomainId(null);
    setViewOverride('add');
  };

  const handleBackFromAdd = () => {
    clearViewOverride();
    if (domains.length === 0) {
      setViewOverride('empty');
    }
  };

  const handleCreated = useCallback(() => {
    setSelectedDomainId(null);
    clearViewOverride();

    const wasEmpty = domains.length === 0;
    if (wasEmpty) {
      setIsRefreshingAfterCreate(true);
    }

    void refetch().finally(() => {
      if (wasEmpty) {
        setIsRefreshingAfterCreate(false);
      }
    });
  }, [clearViewOverride, domains.length, refetch]);

  const handleOpenDetail = (domainId: string) => {
    setSelectedDomainId(domainId);
    clearViewOverride();
  };

  const handleBackToList = () => {
    setSelectedDomainId(null);
    clearViewOverride();
  };

  const handleDeleted = useCallback(() => {
    handleBackToList();
    void refetch();
  }, [handleBackToList, refetch]);

  const handleRequestDelete = useCallback(
    (domain: EmailSendingDomain) => {
      openModal({
        renderContent: DeleteEmailSendingDomainModal,
        renderContentProps: {
          domain,
          deleteFlowId,
          connectorInstanceId,
          onDeleted: handleDeleted,
        },
        dismissable: true,
        fullWidth: false,
        fullHeight: false,
      });
    },
    [connectorInstanceId, deleteFlowId, handleDeleted, openModal]
  );

  if (!isConnectorsLoading && !isMailtrapConnected) {
    return <EmailCampaignGateCard variant='connect-mailtrap' />;
  }

  if (!isConnectorsLoading && isMailtrapConnected && missingAudience) {
    return <EmailCampaignGateCard variant='no-audience' />;
  }

  if (isError && !isAddView && resolvedView !== 'detail') {
    return <EmailSendingDomainsErrorCard onRetry={() => void refetch()} />;
  }

  if (showPageLoading && !isAddView && resolvedView !== 'detail') {
    return <Loading />;
  }

  if (resolvedView === 'empty' && isMailtrapConnected && !showPageLoading) {
    return <EmailSendingDomainsEmptyCard onAdd={handleOpenAdd} />;
  }

  if (resolvedView === 'add') {
    return <EmailSendingDomainInsertFlow onBack={handleBackFromAdd} onCreated={handleCreated} />;
  }

  if (resolvedView === 'detail') {
    return (
      <EmailSendingDomainDetailView
        domainId={selectedDomainId}
        domainNameHint={selectedDomainNameHint}
        deleteFlowId={deleteFlowId}
        connectorInstanceId={connectorInstanceId}
        onBack={handleBackToList}
        onDeleted={handleDeleted}
      />
    );
  }

  return (
    <section className='bg-surface space-y-5 rounded-3xl border border-secondary-border p-5 shadow-sm md:p-6'>
      <EmailSendingDomainsList
        domains={domains}
        isFetching={isFetching}
        onAdd={handleOpenAdd}
        onSelectDomain={handleOpenDetail}
        onRequestDelete={handleRequestDelete}
      />
    </section>
  );
};
